import React, { useState, useEffect } from 'react';
import { api } from '../../api/api';
import { TrashIcon } from '../common';
import { useAuth } from '../../auth/AuthContext';

const TemplateEditor = ({ currentSettings, showNotification }) => {
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'Admin';
  const [templates, setTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTargetEvent, setBulkTargetEvent] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedTemplates, setDeletedTemplates] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: '',
    visibility: 'global',
  });
  const [editingCategories, setEditingCategories] = useState({});
  const [editingNames, setEditingNames] = useState({});

  const fetchAllTemplates = async () => {
    setLoading(true);
    const data = await api.getAllTemplatesForAdmin();
    setTemplates(data.global);
    setUserTemplates(data.user);
    setLoading(false);
  };

  // Removed unused fetchUsers/fetchGroups/fetchEvents helpers (inlined via Promise.all below)
  const fetchDeleted = async () => {
    try {
      const dels = await api.getDeletedTemplates();
      setDeletedTemplates(dels);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [all, userList, groupsData, evts] = await Promise.all([
          api.getAllTemplatesForAdmin(),
          api.getUsers(),
          api.getGroups(),
          api.getEvents(),
        ]);
        setTemplates(all.global);
        setUserTemplates(all.user);
        setUsers(userList);
        setGroups(groupsData);
        setEvents(isAdmin ? evts : evts.filter(e => e.status !== 'archived'));
      } catch (e) {
        // ignore; individual UIs handle errors via notifications
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  const handleSaveCurrentAsNew = async () => {
    if (!newTemplate.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    const { id, ...settingsWithoutId } = currentSettings;
    const templateData = {
      ...settingsWithoutId,
      name: newTemplate.name,
      category: newTemplate.category || null, // Allow null/empty categories
      visibility: newTemplate.visibility,
    };

    const result = await api.saveGlobalTemplate(templateData);

    if (result.success) {
      showNotification(`Template "${newTemplate.name}" created!`, 'success');
      setShowCreateForm(false);
      setNewTemplate({ name: '', category: '', visibility: 'global' });
      fetchAllTemplates();
    } else {
      showNotification(result.message || 'Failed to create template', 'error');
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete the "${templateName}" template?`
      )
    ) {
      // Find the template to determine its type
      const templateToDelete =
        templates.find(t => t.id === templateId) ||
        userTemplates.find(t => t.id === templateId);

      let result;
      if (
        templateToDelete &&
        (templateToDelete.visibility === 'global' ||
          templateToDelete.visibility.startsWith('group-'))
      ) {
        result = await api.deleteGlobalTemplate(templateId);
      } else {
        result = await api.deleteUserTemplate(null, templateId);
      }

      if (result.success) {
        showNotification('Template deleted.', 'success');
        fetchAllTemplates();
      } else {
        showNotification(
          result.message || 'Failed to delete template',
          'error'
        );
      }
    }
  };

  const handleTemplateUpdate = async (templateId, field, value) => {
    // Find template in both global and user templates
    const templateToUpdate =
      templates.find(t => t.id === templateId) ||
      userTemplates.find(t => t.id === templateId);

    // Validate that we have a valid template with a valid ID
    if (
      !templateToUpdate ||
      !templateToUpdate.id ||
      templateToUpdate.id === null ||
      templateToUpdate.id === 'null' ||
      templateToUpdate.id === 'none'
    ) {
      showNotification('Cannot update template: Invalid template ID', 'error');
      return;
    }

    // Ensure we have a valid numeric ID
    const numericId = parseInt(templateToUpdate.id);
    if (isNaN(numericId)) {
      showNotification(
        'Cannot update template: Invalid template ID format',
        'error'
      );
      return;
    }

    // Create a complete update object with all template data
    const updateData = {
      id: numericId,
      name: templateToUpdate.name || '',
      category: templateToUpdate.category || '',
      visibility: templateToUpdate.visibility || 'user',
      // Include all the template configuration data
      ...templateToUpdate,
    };

    // Update the specific field
    updateData[field] = value;

    // If we're changing visibility to 'user', ensure we have a valid owner_id
    if (field === 'visibility' && value === 'user') {
      if (
        templateToUpdate.ownerId &&
        !isNaN(parseInt(templateToUpdate.ownerId))
      ) {
        updateData.owner_id = parseInt(templateToUpdate.ownerId);
      } else {
        // If no valid owner_id, we can't change to user visibility
        showNotification(
          'Cannot change to user visibility: template has no valid owner',
          'error'
        );
        return;
      }
    } else if (
      templateToUpdate.ownerId &&
      !isNaN(parseInt(templateToUpdate.ownerId))
    ) {
      // Include owner_id for other updates if it exists
      updateData.owner_id = parseInt(templateToUpdate.ownerId);
    }

    // Use appropriate update function based on the NEW visibility value
    let result;
    if (
      updateData.visibility === 'global' ||
      updateData.visibility.startsWith('group-')
    ) {
      result = await api.updateGlobalTemplate(updateData);
    } else {
      result = await api.updateUserTemplate(updateData);
    }

    if (result.success) {
      fetchAllTemplates();
      showNotification('Template updated!', 'success');
    } else {
      showNotification(result.message || 'Failed to update template', 'error');
    }
  };

  const handleCategoryChange = (templateId, value) => {
    setEditingCategories(prev => ({
      ...prev,
      [templateId]: value,
    }));
  };

  const handleCategoryBlur = async templateId => {
    const newValue = editingCategories[templateId];
    if (newValue !== undefined) {
      await handleTemplateUpdate(templateId, 'category', newValue);
      setEditingCategories(prev => {
        const updated = { ...prev };
        delete updated[templateId];
        return updated;
      });
    }
  };

  const handleNameChange = (templateId, value) => {
    setEditingNames(prev => ({
      ...prev,
      [templateId]: value,
    }));
  };

  const handleNameBlur = async templateId => {
    const newValue = editingNames[templateId];
    if (newValue !== undefined && newValue.trim() !== '') {
      await handleTemplateUpdate(templateId, 'name', newValue.trim());
      setEditingNames(prev => {
        const updated = { ...prev };
        delete updated[templateId];
        return updated;
      });
    } else {
      // If empty, just clear the editing state without saving
      setEditingNames(prev => {
        const updated = { ...prev };
        delete updated[templateId];
        return updated;
      });
    }
  };

  const getUsernameById = id => {
    const user = users.find(u => u.id === id);
    return user ? user.username : 'Unknown';
  };

  const getOwnerUsername = template => {
    return (
      template.ownerUsername || getUsernameById(template.ownerId) || 'Unknown'
    );
  };

  const getVisibilityLabel = visibility => {
    if (visibility === 'global') return 'Global';
    if (visibility.startsWith('group-')) {
      const groupId = visibility.replace('group-', '');
      const group = groups.find(g => g.id.toString() === groupId);
      return group ? `Group: ${group.name}` : visibility;
    }
    return visibility;
  };

  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = list => {
    const ids = list.map(t => t.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const targetEventIsArchived = () => {
    const evt = events.find(e => String(e.id) === String(bulkTargetEvent));
    return !!(evt && evt.status === 'archived');
  };

  const handleBulkCopy = async () => {
    const ids = Array.from(selectedIds);
    if (!bulkTargetEvent)
      return showNotification('Select a target event', 'error');
    if (!ids.length) return showNotification('Select templates first', 'error');
    if (targetEventIsArchived())
      return showNotification('Target event is archived', 'error');
    const target = events.find(e => String(e.id) === String(bulkTargetEvent));
    const name = target ? target.name : 'selected event';
    if (!window.confirm(`Copy ${ids.length} template(s) to "${name}"?`)) return;
    try {
      await api.bulkCopyTemplates(ids, parseInt(bulkTargetEvent));
      showNotification('Templates copied', 'success');
    } catch (e) {
      showNotification('Bulk copy failed', 'error');
    }
  };

  const handleBulkMove = async () => {
    const ids = Array.from(selectedIds);
    if (!bulkTargetEvent)
      return showNotification('Select a target event', 'error');
    if (!ids.length) return showNotification('Select templates first', 'error');
    if (targetEventIsArchived())
      return showNotification('Target event is archived', 'error');
    const target = events.find(e => String(e.id) === String(bulkTargetEvent));
    const name = target ? target.name : 'selected event';
    if (
      !window.confirm(
        `Move ${ids.length} template(s) to "${name}"? This will change their event.`
      )
    )
      return;
    try {
      await api.bulkMoveTemplates(ids, parseInt(bulkTargetEvent));
      showNotification('Templates moved', 'success');
      setSelectedIds(new Set());
      fetchAllTemplates();
    } catch (e) {
      showNotification('Bulk move failed', 'error');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return showNotification('Select templates first', 'error');
    if (
      !window.confirm(
        `Delete ${ids.length} template(s)? They can be restored within 30 days.`
      )
    )
      return;
    try {
      await api.bulkDeleteTemplates(ids);
      showNotification('Templates deleted', 'success');
      setSelectedIds(new Set());
      fetchAllTemplates();
      if (showDeleted) await fetchDeleted();
    } catch (e) {
      showNotification(e.message || 'Bulk delete failed', 'error');
    }
  };

  if (loading) return <p>Loading templates...</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 p-4 bg-gray-50 border rounded-lg">
        <div>
          <h3 className="text-lg font-medium">
            Create Template from Current Config
          </h3>
          <p className="text-sm text-gray-500">
            Saves the current Configurator settings as a new template. For exact
            copies of existing templates (including hidden fields), use
            Duplicate on the template row.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="nav-button-primary flex-shrink-0"
          >
            {showCreateForm ? 'Cancel' : 'Save Current Config as New Template'}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="mb-6 p-4 bg-white border rounded-lg">
          <h4 className="font-medium mb-4">Create New Template</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name
              </label>
              <input
                type="text"
                value={newTemplate.name}
                onChange={e =>
                  setNewTemplate({ ...newTemplate, name: e.target.value })
                }
                className="form-input w-full"
                placeholder="Enter template name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category (Optional)
              </label>
              <input
                type="text"
                value={newTemplate.category}
                onChange={e =>
                  setNewTemplate({ ...newTemplate, category: e.target.value })
                }
                className="form-input w-full"
                placeholder="e.g., General, Registration (leave empty for no category)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <select
                value={newTemplate.visibility}
                onChange={e =>
                  setNewTemplate({ ...newTemplate, visibility: e.target.value })
                }
                className="form-select w-full"
              >
                <option value="global">Global (All Users)</option>
                {groups.map(group => (
                  <option key={group.id} value={`group-${group.id}`}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSaveCurrentAsNew}
              className="nav-button-primary"
            >
              Create Template
            </button>
            <button
              onClick={() => setShowCreateForm(false)}
              className="nav-button-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3 bg-gray-50 border rounded px-3 py-2">
          <span className="text-xs text-gray-600">
            {selectedIds.size} selected
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Target event</label>
            <select
              className="form-select text-xs"
              value={bulkTargetEvent}
              onChange={e => setBulkTargetEvent(e.target.value)}
            >
              <option value="">Choose…</option>
              {events.map(evt => (
                <option
                  key={evt.id}
                  value={String(evt.id)}
                  disabled={evt.status === 'archived'}
                >
                  {evt.name}
                  {evt.status === 'archived' ? ' (Archived)' : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            className="px-3 py-1 text-xs rounded border hover:bg-gray-100 disabled:opacity-50"
            onClick={handleBulkCopy}
            disabled={
              !selectedIds.size || !bulkTargetEvent || targetEventIsArchived()
            }
          >
            Copy
          </button>
          <button
            className="px-3 py-1 text-xs rounded border hover:bg-gray-100 disabled:opacity-50"
            onClick={handleBulkMove}
            disabled={
              !selectedIds.size || !bulkTargetEvent || targetEventIsArchived()
            }
          >
            Move
          </button>
          <button
            className="px-3 py-1 text-xs rounded border text-red-600 hover:bg-red-50 disabled:opacity-50"
            onClick={handleBulkDelete}
            disabled={!selectedIds.size}
          >
            Delete
          </button>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={async e => {
              setShowDeleted(e.target.checked);
              if (e.target.checked) {
                await fetchDeleted();
                if (!deletedTemplates.length) {
                  showNotification('No deleted templates found', 'info');
                }
              }
            }}
          />
          Show deleted
        </label>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Manage Existing Global & Group Templates
        </h3>
        {templates.length > 0 && (
          <button
            className="text-sm text-blue-600"
            onClick={() => toggleSelectAll(templates)}
          >
            Toggle Select All
          </button>
        )}
      </div>
      <div className="space-y-3 mb-10">
        {templates.length > 0 ? (
          templates.map(t => (
            <div
              key={t.id}
              className="p-4 bg-white border rounded-lg grid grid-cols-6 gap-4 items-center"
            >
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={selectedIds.has(t.id)}
                  onChange={() => toggleSelect(t.id)}
                />
              </div>
              <div className="col-span-2">
                <div className="mb-1">
                  <input
                    type="text"
                    value={
                      editingNames[t.id] !== undefined
                        ? editingNames[t.id]
                        : t.name || ''
                    }
                    onChange={e => handleNameChange(t.id, e.target.value)}
                    onBlur={() => handleNameBlur(t.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleNameBlur(t.id);
                      }
                    }}
                    className="font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-1 w-full"
                    placeholder="Template name"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Created by: {getOwnerUsername(t)}
                </p>
                <p className="text-xs text-gray-400">
                  {getVisibilityLabel(t.visibility)}
                </p>
              </div>
              <div className="col-span-1">
                <label className="text-xs text-gray-500">Category</label>
                <input
                  type="text"
                  value={
                    editingCategories[t.id] !== undefined
                      ? editingCategories[t.id]
                      : t.category || ''
                  }
                  onChange={e => handleCategoryChange(t.id, e.target.value)}
                  onBlur={() => handleCategoryBlur(t.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleCategoryBlur(t.id);
                    }
                  }}
                  className="form-input text-sm py-1"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-gray-500">Visibility</label>
                <select
                  value={t.visibility || 'global'}
                  onChange={e =>
                    handleTemplateUpdate(t.id, 'visibility', e.target.value)
                  }
                  className="form-select text-sm py-1"
                >
                  <option value="user">User Only</option>
                  <option value="global">Global</option>
                  {groups.map(group => (
                    <option key={group.id} value={`group-${group.id}`}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 flex justify-end gap-2">
                <button
                  className="px-2 py-1 text-xs rounded border hover:bg-gray-100"
                  onClick={async () => {
                    const name = window.prompt(
                      'New template name',
                      `${t.name} Copy`
                    );
                    if (!name) return;
                    const res = await api.duplicateTemplate(
                      t.id,
                      undefined,
                      name
                    );
                    if (res.success) {
                      showNotification('Template duplicated', 'success');
                      fetchAllTemplates();
                    } else {
                      showNotification(
                        res.message || 'Duplicate failed',
                        'error'
                      );
                    }
                  }}
                >
                  Duplicate
                </button>
                <button
                  onClick={() => handleDelete(t.id, t.name)}
                  className="p-2 rounded-full hover:bg-red-100 text-red-600"
                  title="Delete"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">No global templates found.</p>
        )}
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-900">User Templates</h3>
        {userTemplates.length > 0 && (
          <button
            className="text-sm text-blue-600"
            onClick={() => toggleSelectAll(userTemplates)}
          >
            Toggle Select All
          </button>
        )}
      </div>
      <div className="space-y-3">
        {userTemplates.length > 0 ? (
          userTemplates.map(t => (
            <div
              key={t.id}
              className="p-4 bg-gray-50 border rounded-lg grid grid-cols-6 gap-4 items-center"
            >
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={selectedIds.has(t.id)}
                  onChange={() => toggleSelect(t.id)}
                />
              </div>
              <div className="col-span-2">
                <div className="mb-1">
                  <input
                    type="text"
                    value={
                      editingNames[t.id] !== undefined
                        ? editingNames[t.id]
                        : t.name || ''
                    }
                    onChange={e => handleNameChange(t.id, e.target.value)}
                    onBlur={() => handleNameBlur(t.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleNameBlur(t.id);
                      }
                    }}
                    className="font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-1 w-full"
                    placeholder="Template name"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Created by: {getOwnerUsername(t)}
                </p>
                <p className="text-xs text-gray-400">
                  {getVisibilityLabel(t.visibility)}
                </p>
              </div>
              <div className="col-span-1">
                <label className="text-xs text-gray-500">Category</label>
                <input
                  type="text"
                  value={
                    editingCategories[t.id] !== undefined
                      ? editingCategories[t.id]
                      : t.category || ''
                  }
                  onChange={e => handleCategoryChange(t.id, e.target.value)}
                  onBlur={() => handleCategoryBlur(t.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleCategoryBlur(t.id);
                    }
                  }}
                  className="form-input text-sm py-1"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-gray-500">Visibility</label>
                <select
                  value={t.visibility || 'user'}
                  onChange={e =>
                    handleTemplateUpdate(t.id, 'visibility', e.target.value)
                  }
                  className="form-select text-sm py-1"
                >
                  <option value="user">User Only</option>
                  <option value="global">Global</option>
                  {groups.map(group => (
                    <option key={group.id} value={`group-${group.id}`}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 flex justify-end gap-2">
                <button
                  className="px-2 py-1 text-xs rounded border hover:bg-gray-100"
                  onClick={async () => {
                    const name = window.prompt(
                      'New template name',
                      `${t.name} Copy`
                    );
                    if (!name) return;
                    const res = await api.duplicateTemplate(
                      t.id,
                      undefined,
                      name
                    );
                    if (res.success) {
                      showNotification('Template duplicated', 'success');
                      fetchAllTemplates();
                    } else {
                      showNotification(
                        res.message || 'Duplicate failed',
                        'error'
                      );
                    }
                  }}
                >
                  Duplicate
                </button>
                <button
                  onClick={() => handleDelete(t.id, t.name)}
                  className="p-2 rounded-full hover:bg-red-100 text-red-600"
                  title="Delete"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">No user templates found.</p>
        )}
      </div>

      {showDeleted && (
        <div className="mt-10">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Recently Deleted
          </h3>
          <div className="space-y-3">
            {deletedTemplates.length ? (
              deletedTemplates.map(t => (
                <div
                  key={`deleted-${t.id}`}
                  className="p-3 bg-white border rounded flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-gray-500">
                      Deleted{' '}
                      {t.deletedAt
                        ? new Date(t.deletedAt).toLocaleString()
                        : ''}{' '}
                      • Owner: {getOwnerUsername(t)}
                    </div>
                  </div>
                  <div>
                    <button
                      className="px-3 py-1 text-sm rounded bg-green-100 text-green-700 hover:bg-green-200"
                      onClick={async () => {
                        const res = await api.restoreTemplate(t.id);
                        if (res.success) {
                          showNotification('Template restored', 'success');
                          fetchDeleted();
                          fetchAllTemplates();
                        } else
                          showNotification(
                            res.message || 'Restore failed',
                            'error'
                          );
                      }}
                    >
                      Restore
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No deleted templates.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
